/**
 * ResearchSearchBox — reusable header search across the entire TCP
 * Research Explorer graph (vaccines + ingredients + diseases +
 * manufacturers + surveillance systems).
 *
 * Standalone — doesn't depend on existing /library or /search infra.
 * Future explorers (Medicines, Chemicals) will be added to the same
 * endpoint and appear automatically.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Syringe, FlaskConical, Activity, Building2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";

const KIND_ICON = {
    vaccine: Syringe,
    ingredient: FlaskConical,
    disease: Activity,
    manufacturer: Building2,
    surveillance_system: AlertTriangle,
};

const KIND_LABEL = {
    vaccine: "Vaccine",
    ingredient: "Ingredient",
    disease: "Disease",
    manufacturer: "Manufacturer",
    surveillance_system: "Surveillance",
};

export default function ResearchSearchBox({ autoFocus = false, placeholder = "Search vaccines, ingredients, diseases…" }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
    }, [autoFocus]);

    // Close on outside click.
    useEffect(() => {
        const onDocClick = (e) => {
            if (!containerRef.current?.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Debounced search.
    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) { setResults([]); return; }
        setBusy(true);
        const t = setTimeout(() => {
            api.get(`/research/search?q=${encodeURIComponent(term)}`)
                .then(({ data }) => setResults(data.results || []))
                .catch(() => setResults([]))
                .finally(() => setBusy(false));
        }, 180);
        return () => clearTimeout(t);
    }, [q]);

    const select = (r) => {
        setOpen(false);
        setQ("");
        navigate(r.href);
    };

    return (
        <div ref={containerRef} className="relative w-full max-w-md" data-testid="research-search">
            <div className="flex items-center gap-2 bg-[#111827] border border-[#243044] rounded-lg px-3 py-2 focus-within:border-[#56CCF2]/60">
                <Search className="w-4 h-4 text-[#64748B]" />
                <input
                    ref={inputRef}
                    type="text"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => q.trim().length >= 2 && setOpen(true)}
                    placeholder={placeholder}
                    data-testid="research-search-input"
                    className="bg-transparent border-0 outline-none text-sm text-white placeholder:text-[#475569] flex-1"
                />
                {q ? (
                    <button type="button" onClick={() => { setQ(""); setResults([]); }} className="text-[#64748B] hover:text-white">
                        <X className="w-3.5 h-3.5" />
                    </button>
                ) : null}
            </div>
            {open && q.trim().length >= 2 ? (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#0B1220] border border-[#243044] rounded-lg shadow-2xl max-h-96 overflow-y-auto z-50" data-testid="research-search-results">
                    {busy && results.length === 0 ? (
                        <div className="px-3 py-3 text-[12px] text-[#64748B]">Searching…</div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-3 text-[12px] text-[#64748B] italic">No matches.</div>
                    ) : results.map((r, i) => {
                        const Icon = KIND_ICON[r.kind] || Search;
                        return (
                            <button
                                key={`${r.kind}-${r.slug}-${i}`}
                                onClick={() => select(r)}
                                data-testid={`research-search-result-${r.kind}-${r.slug}`}
                                className="w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-[#111827] transition-colors border-b border-[#243044] last:border-b-0"
                            >
                                <Icon className="w-3.5 h-3.5 text-[#56CCF2] mt-0.5 shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-sm text-white truncate">{r.title}</span>
                                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#243044] text-[#94A3B8]">{KIND_LABEL[r.kind] || r.kind}</span>
                                    </div>
                                    {r.subtitle ? <div className="text-[11px] text-[#64748B] truncate mt-0.5">{r.subtitle}</div> : null}
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
